import React, { useState, useMemo, useEffect, useRef } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Flag } from "@/lib/countries";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Caustics } from "@/components/SwimmingFX";
import { Search, X, Trophy, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/* ── Stroke → image mapping (same sources as StrokeShowcase) ── */
const STROKE_IMG = {
  freestyle:    "https://images.pexels.com/photos/863988/pexels-photo-863988.jpeg?auto=compress&cs=tinysrgb&w=400",
  backstroke:   "https://images.unsplash.com/photo-1530549387789-4c1017266635?w=400&q=80",
  breaststroke: "https://images.pexels.com/photos/261185/pexels-photo-261185.jpeg?auto=compress&cs=tinysrgb&w=400",
  butterfly:    "https://images.pexels.com/photos/73760/swimming-swimmer-female-race-73760.jpeg?auto=compress&cs=tinysrgb&w=400",
  medley:       "https://images.unsplash.com/photo-1519315901367-f34ff9154487?w=400&q=80",
  relay:        "https://images.pexels.com/photos/863988/pexels-photo-863988.jpeg?auto=compress&cs=tinysrgb&w=400",
};

const STROKE_COLOR = {
  freestyle: "#00F0FF", backstroke: "#7DD3FC", breaststroke: "#34D399",
  butterfly: "#FBBF24", medley: "#F472B6", relay: "#A78BFA",
};

function getStroke(eventName = "") {
  const n = eventName.toLowerCase();
  if (n.includes("relay"))        return "relay";
  if (n.includes("medley") || n.includes(" im")) return "medley";
  if (n.includes("butterfly") || n.includes("butter fly")) return "butterfly";
  if (n.includes("breaststroke") || n.includes("breast stroke")) return "breaststroke";
  if (n.includes("backstroke") || n.includes("back stroke"))   return "backstroke";
  return "freestyle";
}

function getAgeCategory(eventName = "") {
  const old = eventName.match(/(\d+)\s+Year\s+Olds/i);
  if (old) return `${old[1]} Year Olds`;
  const masters = eventName.match(/\b(\d{2}\s*-\s*\d{2}|\d{2}\s*&\s*Over)\b/i);   // "25-29", "80 & Over"
  return masters ? masters[1].replace(/\s*-\s*/, "-").replace(/\s*&\s*/, " & ") : null;
}

/* Sort key so cards read Event 1 → 2 → 3, men before women, youngest age group first */
const ageStart = (name) => parseInt((getAgeCategory(name) || "999").match(/\d+/)?.[0] ?? "999", 10);

const STROKE_LABELS = {
  freestyle: "Freestyle", backstroke: "Backstroke", breaststroke: "Breaststroke",
  butterfly: "Butterfly", medley: "Medley / IM", relay: "Relay",
};

/* ── Demo data — structured to mirror Schedule page ── */
const DAYS = [
  { day: 1, label: "Day 1", date: "2026-10-04" },
  { day: 2, label: "Day 2", date: "2026-10-05" },
  { day: 3, label: "Day 3", date: "2026-10-06" },
  { day: 4, label: "Day 4", date: "2026-10-07" },
  { day: 5, label: "Day 5", date: "2026-10-08" },
  { day: 6, label: "Day 6", date: "2026-10-09" },
  { day: 7, label: "Day 7", date: "2026-10-10" },
];


const MEDAL = {
  1: { label: "Gold",   glow: "shadow-[0_8px_48px_rgba(251,191,36,0.32)]",  border: "border-[#FBBF24]/40", bg: "from-[#FBBF24]/[0.10]", num: "text-[#FBBF24]", animDelay: 0.55 },
  2: { label: "Silver", glow: "shadow-[0_8px_36px_rgba(148,163,184,0.26)]", border: "border-[#94A3B8]/30", bg: "from-[#94A3B8]/[0.07]", num: "text-[#94A3B8]", animDelay: 0.3  },
  3: { label: "Bronze", glow: "shadow-[0_8px_32px_rgba(249,115,22,0.22)]",  border: "border-[#F97316]/30", bg: "from-[#F97316]/[0.07]", num: "text-[#F97316]", animDelay: 0.1  },
};

export default function Results() {
  const [selectedDay,       setSelectedDay]       = useState(1);
  const [selectedEvent,     setSelectedEvent]     = useState(null);
  const [strokeFilter,      setStrokeFilter]      = useState("all");
  const [ageCatFilter,      setAgeCatFilter]      = useState("all");
  const [genderFilter,      setGenderFilter]      = useState("all");
  const [search,            setSearch]            = useState("");
  const [liveResults,       setLiveResults]       = useState([]);
  const [loading,           setLoading]           = useState(false);
  const [resultPdfs,        setResultPdfs]        = useState({});
  const tabRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();

  /* Which events have an official results PDF to download */
  useEffect(() => {
    api.get(`/results/pdfs?day=${selectedDay}`).then(({ data }) => setResultPdfs(data || {})).catch(() => setResultPdfs({}));
  }, [liveResults, selectedDay]);

  const selectedEventNumber = liveResults.find(r => r.event_name === selectedEvent)?.event_number;
  const eventPdfs = selectedEventNumber != null ? (resultPdfs[String(selectedEventNumber)] || []) : [];

  const downloadPdf = async (pdf) => {
    try {
      const res = await api.get(`/results/pdf/${pdf.pdf_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = pdf.filename || `${pdf.label}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the PDF");
    }
  };

  /* Fetch live results when day changes */
  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/results?day=${selectedDay}`);
        setLiveResults(data);
      } catch (err) {
        console.error("Failed to fetch results", err);
        setLiveResults([]);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [selectedDay]);

  /* All event names for this day */
  const dayEvents = useMemo(() => {
    const meta = new Map();
    liveResults.forEach(r => { if (!meta.has(r.event_name)) meta.set(r.event_name, { n: r.event_number ?? 0, g: r.gender || "" }); });
    return [...meta.keys()].sort((a, b) => {
      const A = meta.get(a), B = meta.get(b);
      return A.n - B.n || A.g.localeCompare(B.g) || ageStart(a) - ageStart(b);
    });
  }, [liveResults]);

  /* Available filter options derived from dayEvents */
  const availableAgeCategories = useMemo(() =>
    [...new Set(dayEvents.map(getAgeCategory).filter(Boolean))].sort(
      (a, b) => parseInt(a) - parseInt(b)
    ), [dayEvents]);

  const availableGenders = useMemo(() =>
    [...new Set(liveResults.map(r => r.gender).filter(Boolean))].sort(), [liveResults]);

  /* Events after stroke / age / gender filters */
  const filteredDayEvents = useMemo(() =>
    dayEvents.filter(evName => {
      if (strokeFilter  !== "all" && getStroke(evName) !== strokeFilter)      return false;
      if (ageCatFilter  !== "all" && getAgeCategory(evName) !== ageCatFilter) return false;
      if (genderFilter  !== "all") {
        const g = liveResults.find(r => r.event_name === evName)?.gender || "";
        if (g.toLowerCase() !== genderFilter.toLowerCase()) return false;
      }
      return true;
    }), [dayEvents, strokeFilter, ageCatFilter, genderFilter, liveResults]);

  /* Auto-select first visible event when filters or results change */
  useEffect(() => {
    setSelectedEvent(prev =>
      filteredDayEvents.includes(prev) ? prev : (filteredDayEvents[0] || null)
    );
  }, [filteredDayEvents]);

  const rankNum = (r) => {
    if (r.rank === "---") return 9998;
    const n = parseInt(r.rank.replace("*", ""));
    return isNaN(n) ? 9999 : n;
  };

  /* Results for selected event, filtered + sorted by rank */
  const eventResults = useMemo(() => {
    if (!selectedEvent) return [];
    const q = search.toLowerCase();
    return liveResults
      .filter(r => r.event_name === selectedEvent)
      .filter(r => !q || r.athlete.toLowerCase().includes(q) || r.team.toLowerCase().includes(q))
      .slice()
      .sort((a, b) => rankNum(a) - rankNum(b));
  }, [liveResults, selectedEvent, search]);
  const top3   = eventResults.filter(r => rankNum(r) <= 3 && !r.rank.startsWith("*") && r.rank !== "---");
  const restOf = eventResults.filter(r => rankNum(r) > 3 || r.rank.startsWith("*") || r.rank === "---");

  /* Podium order: 2nd | 1st | 3rd */
  const podiumOrder = [
    top3.find(r => rankNum(r) === 2),
    top3.find(r => rankNum(r) === 1),
    top3.find(r => rankNum(r) === 3),
  ].filter(Boolean);

  const hasEventFilters = strokeFilter !== "all" || ageCatFilter !== "all" || genderFilter !== "all";
  const hasFilters      = hasEventFilters || search;
  const clearEventFilters = () => { setStrokeFilter("all"); setAgeCatFilter("all"); setGenderFilter("all"); };

  return (
    <div className="min-h-dvh bg-[#0B1429] text-white">
      <Navbar />

      {/* ── Hero ── */}
      <div className="relative pt-32 pb-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F2040] via-[#0B1429] to-[#0B1429]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-56 bg-[#00F0FF]/[0.06] blur-[90px] rounded-full pointer-events-none" />
        {!shouldReduceMotion && <Caustics opacity={0.07} />}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#00F0FF] mb-3">
              AMSC India 2026 · Official Programme
            </p>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h1 className="font-display font-black text-4xl md:text-5xl">
                Competition <span className="text-[#00F0FF]">Results</span>
              </h1>
              <div className="flex items-center gap-2 pb-1">
                <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                <span className="text-xs font-bold text-[#10B981] uppercase tracking-widest">Live</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">

        {/* ── Day tabs ── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {DAYS.map(d => {
                const hasDayResults = d.day === selectedDay ? liveResults.length > 0 : false;
                const isSelected = d.day === selectedDay;
                return (
                  <button
                    key={d.day}
                    onClick={() => { setSelectedDay(d.day); }}
                    aria-pressed={isSelected}
                    className={[
                      "relative flex flex-col items-center shrink-0 min-w-[72px] px-4 py-4 rounded-xl border font-display font-bold text-sm transition-all duration-200 cursor-pointer touch-manipulation select-none",
                      isSelected
                        ? "bg-[#00F0FF]/10 border-[#00F0FF]/60 text-[#00F0FF] shadow-[0_0_20px_rgba(0,240,255,0.15)]"
                        : hasDayResults
                        ? "bg-[#142040]/60 border-white/[0.07] text-[#64748B] hover:border-white/[0.18] hover:text-[#94A3B8]"
                        : "bg-[#0F1830]/40 border-white/[0.04] text-white/20 cursor-default",
                    ].join(" ")}
                  >
                    <span className="text-[10px] tracking-[0.18em] uppercase font-semibold opacity-70 mb-0.5 tabular-nums">
                      {d.date.slice(5).replace("-", "/")}
                    </span>
                    <span className="leading-none">{d.label}</span>
                    {!hasDayResults && (
                      <span className="text-[9px] mt-0.5 opacity-40 font-normal">TBD</span>
                    )}
                  </button>
                );
              })}
          </div>
        </motion.div>

        {/* ── Event image cards ── */}
        {dayEvents.length > 0 ? (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.15 }}
              className="mb-6"
            >
              {/* ── Event filter bar ── */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#64748B]">
                  Events — {DAYS.find(d => d.day === selectedDay)?.label}
                </span>
                <div className="flex flex-wrap gap-2 ml-auto">
                  {/* Gender filter */}
                  {availableGenders.length > 1 && (
                    <FilterPill value={genderFilter} onChange={setGenderFilter} options={[
                      { value: "all", label: "All Genders" },
                      ...availableGenders.map(g => ({ value: g, label: g })),
                    ]} />
                  )}
                  {/* Stroke filter */}
                  <FilterPill value={strokeFilter} onChange={setStrokeFilter} options={[
                    { value: "all", label: "All Strokes" },
                    ...Object.entries(STROKE_LABELS)
                      .filter(([key]) => dayEvents.some(e => getStroke(e) === key))
                      .map(([key, label]) => ({ value: key, label })),
                  ]} />
                  {/* Age category filter */}
                  {availableAgeCategories.length > 0 && (
                    <FilterPill value={ageCatFilter} onChange={setAgeCatFilter} options={[
                      { value: "all", label: "All Ages" },
                      ...availableAgeCategories.map(a => ({ value: a, label: a })),
                    ]} />
                  )}
                  {hasEventFilters && (
                    <button onClick={clearEventFilters} className="h-8 px-3 rounded-full border border-white/[0.08] text-xs text-[#64748B] hover:text-white hover:border-white/20 transition-colors cursor-pointer flex items-center gap-1">
                      <X className="w-3 h-3" /> Clear
                    </button>
                  )}
                </div>
              </div>

              <div
                ref={tabRef}
                className="flex gap-3 overflow-x-auto pb-2 px-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                {filteredDayEvents.length === 0 ? (
                  <p className="text-sm text-[#64748B] py-4">No events match the selected filters.</p>
                ) : filteredDayEvents.map(eventName => {
                  const stroke = getStroke(eventName);
                  const img    = STROKE_IMG[stroke];
                  const color  = STROKE_COLOR[stroke];
                  // count removed — not shown on card
                  const active = selectedEvent === eventName;

                  return (
                    <button
                      key={eventName}
                      onClick={() => setSelectedEvent(eventName)}
                      className={[
                        "relative shrink-0 w-44 h-28 rounded-2xl overflow-hidden border-2 transition-all duration-250 cursor-pointer touch-manipulation group",
                        active
                          ? "border-[#00F0FF]/70 scale-[1.02]"
                          : "border-white/[0.06] hover:border-white/25 hover:scale-[1.01]",
                      ].join(" ")}
                      style={active ? { boxShadow: `0 0 24px rgba(0,240,255,0.25)` } : {}}
                    >
                      {/* Background image */}
                      <img
                        src={img}
                        alt={eventName}
                        loading="lazy"
                        className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${active ? "scale-110 brightness-75" : "scale-100 brightness-50 group-hover:brightness-60 group-hover:scale-105"}`}
                      />

                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1429]/90 via-[#0B1429]/30 to-transparent" />

                      {/* Active glow line at bottom */}
                      {active && (
                        <motion.div
                          layoutId="event-active-line"
                          className="absolute bottom-0 left-0 right-0 h-0.5"
                          style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
                        />
                      )}

                      {/* Stroke accent dot */}
                      <div
                        className="absolute top-3 right-3 w-2 h-2 rounded-full"
                        style={{ background: color, boxShadow: `0 0 8px ${color}` }}
                      />

                      {/* Text */}
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-white font-bold text-xs leading-tight line-clamp-2">{eventName}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>

            {/* ── Search within selected event ── */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative w-full sm:w-52 sm:ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" strokeWidth={1.5} />
                <input
                  type="text"
                  placeholder="Athlete or team…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  aria-label="Search results"
                  className="h-9 w-full bg-white/[0.04] border border-white/[0.07] rounded-full pl-8 pr-8 text-sm text-white placeholder:text-[#3A4A6A] focus:outline-none focus:border-[#00F0FF]/40 transition-colors"
                />
                {search && (
                  <button onClick={() => setSearch("")} aria-label="Clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors cursor-pointer">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {search && (
                <button onClick={() => setSearch("")} className="text-xs text-[#64748B] hover:text-white transition-colors cursor-pointer px-1">
                  Clear
                </button>
              )}
            </div>

            {/* ── Event results ── */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedEvent + "-" + selectedDay}
                initial={shouldReduceMotion ? {} : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {eventResults.length > 0 ? (
                  <>
                    {/* Event meta */}
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#64748B]">
                        {selectedEvent} · {DAYS.find(d => d.day === selectedDay)?.label}
                      </p>
                      {eventPdfs.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {eventPdfs.map(pdf => (
                            <button
                              key={pdf.pdf_id}
                              onClick={() => downloadPdf(pdf)}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#00F0FF]/30 bg-[#00F0FF]/10 text-[#00F0FF] text-xs font-semibold hover:bg-[#00F0FF]/20 transition-colors cursor-pointer"
                            >
                              <Download className="w-3.5 h-3.5" aria-hidden="true" />
                              {pdf.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Podium */}
                    {top3.length > 0 && (
                      <div className="relative flex items-end justify-center gap-3 md:gap-5 px-4 pb-6 pt-4 mb-8">
                        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0F1E3D]/40 to-transparent rounded-3xl pointer-events-none" />
                        {podiumOrder.map(athlete => {
                          if (!athlete) return null;
                          const m = MEDAL[rankNum(athlete)];
                          const isFirst = rankNum(athlete) === 1;
                          const stroke = getStroke(selectedEvent);
                          return (
                            <motion.div
                              key={athlete.rank}
                              initial={shouldReduceMotion ? {} : { opacity: 0, y: 70 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: m.animDelay, duration: 0.6, type: "spring", damping: 18, stiffness: 90 }}
                              className={[
                                "relative flex flex-col items-center rounded-2xl border backdrop-blur-sm p-4 md:p-6 transition-transform duration-200 hover:-translate-y-1",
                                `bg-gradient-to-b ${m.bg} to-transparent`,
                                m.border, m.glow,
                                isFirst
                                  ? (athlete.is_relay ? "w-40 md:w-56 pb-8 md:pb-10 -mb-4 z-10" : "w-36 md:w-48 pb-8 md:pb-10 -mb-4 z-10")
                                  : (athlete.is_relay ? "w-32 md:w-48" : "w-28 md:w-40"),
                              ].join(" ")}
                            >
                              {/* Stroke-coloured accent dot */}
                              <div
                                className="absolute top-3 right-3 w-2 h-2 rounded-full"
                                style={{ background: STROKE_COLOR[stroke], boxShadow: `0 0 6px ${STROKE_COLOR[stroke]}` }}
                              />

                              <div className={`text-3xl md:text-4xl mb-2 ${isFirst ? "md:text-5xl" : ""}`}>
                                {rankNum(athlete) === 1 ? "🥇" : rankNum(athlete) === 2 ? "🥈" : "🥉"}
                              </div>

                              <div className={`rounded-full bg-[#142040] border ${m.border} flex items-center justify-center font-black text-white mb-3 ${isFirst ? "w-14 h-14 md:w-16 md:h-16 text-xl" : "w-10 h-10 md:w-12 md:h-12 text-base"}`}>
                                {athlete.athlete[0]}
                              </div>

                              <p className={`text-[10px] font-bold uppercase tracking-[0.18em] mb-1 ${m.num}`}>{m.label}</p>

                              <p className={`font-bold text-white text-center leading-tight mb-1.5 ${isFirst ? "text-sm md:text-base" : "text-xs md:text-sm"}`}>
                                {athlete.athlete}
                              </p>

                              {athlete.is_relay ? (
                                <div className="flex flex-col w-full items-center mt-1 gap-2">
                                  <p className={`font-black tabular-nums font-mono ${m.num} ${isFirst ? "text-base md:text-lg" : "text-sm"}`}>
                                    {athlete.time}
                                  </p>
                                  <div className="flex flex-col w-full text-[10px] md:text-xs text-center text-[#CBD5E1] leading-[1.3]">
                                    {athlete.relay_swimmers?.map((s, idx) => (
                                      <span key={idx} className="truncate w-full block" title={s.name}>{s.name}</span>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <span className="flex items-center gap-1.5 text-[11px] text-[#94A3B8] mb-2">
                                    <Trophy size={12} className="text-[#00F0FF]" />
                                    <span className="hidden md:inline truncate max-w-[100px]">{athlete.team}</span>
                                  </span>

                                  <p className={`font-black tabular-nums font-mono ${m.num} ${isFirst ? "text-base md:text-lg" : "text-sm"}`}>
                                    {athlete.time}
                                  </p>
                                </>
                              )}

                              <div className={`absolute bottom-0 left-4 right-4 h-0.5 rounded-full opacity-60 ${rankNum(athlete) === 1 ? "bg-[#FBBF24]" : rankNum(athlete) === 2 ? "bg-[#94A3B8]" : "bg-[#F97316]"}`} />
                            </motion.div>
                          );
                        })}
                      </div>
                    )}

                    {/* Table: rank 4+ */}
                    {restOf.length > 0 && (
                      <motion.div
                        initial={shouldReduceMotion ? {} : { opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="rounded-2xl bg-[#142040]/40 backdrop-blur-sm border border-white/[0.08] overflow-hidden"
                      >
                        <div className="px-5 py-3 border-b border-white/[0.06]">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#64748B]">
                            Remaining Results
                          </p>
                        </div>
                        <table className="w-full table-fixed">
                          <thead>
                            <tr className="border-b border-white/[0.05]">
                              <th className="px-3 sm:px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B] w-14 sm:w-20">#</th>
                              <th className="px-3 sm:px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B]">Athlete</th>
                              <th className="hidden sm:table-cell px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B] w-32 lg:w-48">Team</th>
                              <th className="px-3 sm:px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B] w-20 sm:w-28">Time</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/[0.04]">
                            {restOf.map((r, i) => (
                              <motion.tr
                                key={`${r.athlete}-${i}`}
                                initial={shouldReduceMotion ? {} : { opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.8 + i * 0.04 }}
                                className="hover:bg-white/[0.02] transition-colors duration-150"
                              >
                                <td className="px-3 sm:px-5 py-3 text-sm text-[#64748B] tabular-nums w-10">{r.rank}</td>
                                <td className="px-3 sm:px-5 py-3 text-sm font-medium text-white truncate">
                                  <span className="block truncate">{r.athlete}</span>
                                  <span className="sm:hidden text-xs text-[#64748B] truncate block max-w-[180px]">{r.team}</span>
                                  {r.is_relay && r.relay_swimmers?.length > 0 && (
                                    <div className="text-sm text-[#CBD5E1] mt-1 flex flex-col leading-tight">
                                      {r.relay_swimmers.map((s, idx) => (
                                        <span key={idx} className="truncate">{s.name}</span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td className="hidden sm:table-cell px-5 py-3 text-sm text-[#94A3B8] truncate max-w-[160px]">{r.team}</td>
                                <td className="px-3 sm:px-5 py-3 text-right text-sm font-bold tabular-nums font-mono text-[#00F0FF]">{r.time}</td>
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </motion.div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-14 h-14 rounded-full bg-[#142040]/60 border border-white/[0.07] flex items-center justify-center mb-4">
                      <Trophy className="w-6 h-6 text-[#3A4A6A]" strokeWidth={1.5} />
                    </div>
                    <p className="text-sm font-semibold text-[#64748B]">
                      {hasFilters ? "No results match your filters" : "Results will appear once the event is completed"}
                    </p>
                    {hasFilters && (
                      <button onClick={() => { clearEventFilters(); setSearch(""); }} className="mt-3 text-xs text-[#00F0FF] hover:text-white transition-colors cursor-pointer">
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </>
        ) : (
          /* Day has no results yet */
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-full bg-[#142040]/60 border border-white/[0.07] flex items-center justify-center mb-4">
              <Trophy className="w-7 h-7 text-[#3A4A6A]" strokeWidth={1.5} />
            </div>
            <p className="text-base font-semibold text-[#64748B]">
              {loading ? "Loading results…" : "Results for this day will appear once competition begins"}
            </p>
          </motion.div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mt-10 text-xs text-[#3A4A6A]">
          <span className="flex items-center gap-1.5"><span className="text-[#FBBF24]">🥇</span> Gold</span>
          <span className="flex items-center gap-1.5"><span className="text-[#94A3B8]">🥈</span> Silver</span>
          <span className="flex items-center gap-1.5"><span className="text-[#F97316]">🥉</span> Bronze</span>
          <span className="sm:ml-auto">All times · IST (UTC+5:30)</span>
        </div>
      </div>

      <Footer />
    </div>
  );
}

const SelectBox = ({ value, onChange, placeholder, children }) => (
  <div className="relative">
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="h-9 bg-white/[0.04] border border-white/[0.07] rounded-full pl-4 pr-8 text-sm text-white focus:outline-none focus:border-[#00F0FF]/40 appearance-none cursor-pointer transition-colors hover:border-white/20"
    >
      <option value="all" className="bg-[#0B1429] text-white">{placeholder}</option>
      {children}
    </select>
    <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-[#64748B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);

const FilterPill = ({ value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={[
        "h-10 md:h-8 border rounded-full pl-3 pr-7 text-xs appearance-none cursor-pointer transition-colors focus:outline-none",
        value !== "all"
          ? "bg-[#00F0FF]/10 border-[#00F0FF]/40 text-[#00F0FF]"
          : "bg-white/[0.04] border-white/[0.08] text-[#94A3B8] hover:border-white/20 hover:text-white",
      ].join(" ")}
    >
      {options.map(o => <option key={o.value} value={o.value} className="bg-[#0B1429] text-white">{o.label}</option>)}
    </select>
    <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-[#64748B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);
